import { useMemo } from "react";
import { getTablePreferences } from "@/api/tablePreferences";
import { UserTablePreferences } from "@/types/ApiResponses/UserTablePreferences";
import { CacheUpdater, createRecordOps } from "@/utils/queryCache";
import { QueryKey, useQuery, useQueryClient } from "@tanstack/react-query";

interface QueryOptions {
  tableKey: string;
}

const createTablePrefsQK = (tableKey: string): QueryKey => {
  return ["table-preferences", tableKey];
};

export const useTablePreferences = (opts: QueryOptions) => {
  return useQuery({
    queryKey: createTablePrefsQK(opts.tableKey),
    queryFn: ({ signal }) => {
      return getTablePreferences({ signal, params: opts });
    },
  });
};

interface TablePreferencesManager {
  get: () => UserTablePreferences | undefined;
  replace: (data: UserTablePreferences) => void;
  patch: (updater: CacheUpdater<UserTablePreferences>) => void;
  invalidate: () => void;
}

export const useTablePreferencesManager = (
  tableKey: string
): TablePreferencesManager => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => createTablePrefsQK(tableKey), [tableKey]);

  const ops = useMemo(
    () => createRecordOps<UserTablePreferences>(queryClient, queryKey),
    [queryClient, queryKey]
  );

  return {
    get: () => ops.get(),
    replace: (data) => ops.replace(data),
    patch: (updater) => ops.patch(updater),
    invalidate: () => ops.invalidate(),
  };
};
